import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtMoney, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Plus, Buildings, TrendUp, CurrencyCircleDollar, Sparkle, Trash, MapPin } from "@phosphor-icons/react";

export default function SellerDashboard() {
  const { user } = useAuth();
  const [props, setProps] = useState([]);
  const [analyzing, setAnalyzing] = useState(null);

  const load = () => api.get("/properties/mine").then(({ data }) => setProps(data));
  useEffect(() => { load(); }, []);

  const runAnalysis = async (p) => {
    setAnalyzing(p.id);
    try {
      await api.post("/ai/patrimony", { property_id: p.id });
      toast.success("Analyse patrimoniale générée !");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAnalyzing(null); }
  };

  const del = async (id) => {
    await api.delete(`/properties/${id}`);
    toast.success("Bien supprimé");
    load();
  };

  const totalValue = props.reduce((s, p) => s + (p.sale_price || 0), 0);
  const monthlyRev = props.reduce((s, p) => s + (p.monthly_rent || 0), 0);
  const chartData = props.slice(0, 6).map((p) => ({ name: p.title.slice(0, 10), value: p.sale_price || p.monthly_rent * 12 || p.event_price_per_day * 30 || 0 }));

  const stats = [
    { i: <Buildings size={22} weight="duotone" />, l: "Biens publiés", v: props.length },
    { i: <CurrencyCircleDollar size={22} weight="duotone" />, l: "Valeur patrimoine", v: fmtMoney(totalValue) },
    { i: <TrendUp size={22} weight="duotone" />, l: "Revenu locatif /mois", v: fmtMoney(monthlyRev) },
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="overline text-xs text-orange-800">Espace Vendeur</span>
            <h1 className="font-display text-4xl font-bold text-stone-900 mt-1">Bonjour, {user?.name?.split(" ")[0]}</h1>
          </div>
          <Link to="/dashboard/add" data-testid="add-property-btn">
            <Button className="rounded-full bg-orange-800 hover:bg-orange-900 text-white px-6 h-11"><Plus weight="bold" className="mr-1" /> Ajouter un bien</Button>
          </Link>
        </div>

        {/* stats */}
        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          {stats.map((s, i) => (
            <div key={i} className="bg-white border border-stone-200 rounded-2xl p-5">
              <span className="w-11 h-11 rounded-xl bg-orange-50 text-orange-800 grid place-items-center">{s.i}</span>
              <div className="text-stone-500 text-sm mt-3">{s.l}</div>
              <div className="font-display text-2xl font-bold text-stone-900">{s.v}</div>
            </div>
          ))}
        </div>

        {chartData.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-2xl p-6 mt-4">
            <h2 className="font-display text-lg font-bold text-stone-900 mb-4">Valeur estimée par bien</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#78716c" }} />
                <YAxis tick={{ fontSize: 11, fill: "#78716c" }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill="#9a3412" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* properties */}
        <h2 className="font-display text-2xl font-bold text-stone-900 mt-10 mb-4">Mes biens</h2>
        {props.length === 0 ? (
          <div className="bg-white border border-dashed border-stone-300 rounded-2xl py-16 text-center text-stone-400" data-testid="dashboard-empty">
            <Buildings size={40} className="mx-auto mb-3 opacity-40" />
            Vous n'avez publié aucun bien. Cliquez sur « Ajouter un bien ».
          </div>
        ) : (
          <div className="space-y-4">
            {props.map((p) => (
              <div key={p.id} data-testid={`dashboard-property-${p.id}`} className="bg-white border border-stone-200 rounded-2xl p-4 flex flex-col sm:flex-row gap-4">
                <img src={p.media?.[0] || "https://images.unsplash.com/photo-1564078516393-cf04bd966897?w=400&q=80"} alt="" className="w-full sm:w-40 h-32 object-cover rounded-xl" />
                <div className="flex-1">
                  <div className="flex items-center gap-1 text-xs text-stone-500"><MapPin size={12} weight="fill" /> {p.city}</div>
                  <h3 className="font-display text-lg font-bold text-stone-900">{p.title}</h3>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {p.modes?.map((m) => <span key={m} className="text-[11px] bg-orange-50 text-orange-800 px-2 py-0.5 rounded-full">{m === "sale" ? "Vente" : m === "longterm" ? "Location" : "Courte durée"}</span>)}
                  </div>
                  {p.ai_analysis && (
                    <div className="text-xs text-stone-500 mt-2 flex items-center gap-3">
                      <span className="text-orange-800 font-semibold flex items-center gap-1"><Sparkle weight="fill" size={12} /> Rendement: {p.ai_analysis.yield_longterm}% • Plus-value 5 ans: {p.ai_analysis.appreciation_5y}%</span>
                    </div>
                  )}
                </div>
                <div className="flex sm:flex-col gap-2 justify-end">
                  <Button data-testid={`analyze-btn-${p.id}`} size="sm" variant="outline" onClick={() => runAnalysis(p)} disabled={analyzing === p.id} className="border-orange-300 text-orange-800">
                    <Sparkle size={15} className="mr-1" /> {analyzing === p.id ? "Analyse…" : "Analyse IA"}
                  </Button>
                  <Button data-testid={`delete-btn-${p.id}`} size="sm" variant="ghost" onClick={() => del(p.id)} className="text-stone-400 hover:text-red-600"><Trash size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
